import "./VettingResultsComponent.scss";
import template from "./VettingResultsComponent.html";
import {IComponentOptions, IFormController, IOnDestroy, IOnInit} from "angular";
import {VettingLevel, VettingMessage, VettingOutcome, VettingOutcomes} from "../../../types/OrderVettingResponse";
import {Observable, ReplaySubject} from "rxjs";
import {UnaryTransform} from "../../../../../../types/GenericTypes";

export const VettingResults = "tfVettingResults";

export function vettingResults(): IComponentOptions {
    return {
        template,
        bindings: {
            outcomes: "<"
        },
        require: {
            form: "?^^form"
        },
        controllerAs: "ctrl",
        controller: VettingResultsComponentController
    };
}

const InitError = "form-initializing";
const VettingError = "vetting-results";

export class VettingResultsComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set outcomes(outcomes: VettingOutcomes | null) {
        if (outcomes != null) {
            this.outcomes$.next(outcomes);
        }
    }

    private readonly outcomes$ = new ReplaySubject<VettingOutcomes>(1);

    /** @require */
    public set form(form: IFormController | null) {
        if (form != null) {
            // Set the form to invalid initially until we know the result of the vetting
            form.$setValidity(InitError, false, form);
            this.form$.next(form);
        }
    }

    private readonly form$ = new ReplaySubject<IFormController>(1);

    public readonly errorOutcomes$: Observable<ReadonlyArray<VettingMessage>>;
    public readonly warningOutcomes$: Observable<ReadonlyArray<VettingMessage>>;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor() {
        this.errorOutcomes$ = this.buildOutcomes(VettingLevel.ERROR);
        this.warningOutcomes$ = this.buildOutcomes(VettingLevel.WARNING);
    }

    private buildOutcomes(level: VettingLevel): Observable<ReadonlyArray<VettingMessage>> {
        return this.outcomes$
                   .map((o) => o.outcomes)
                   .map(this.filterOutcomes(level))
                   .map((o) => this.outcomeDescriptions(o))
                   .publishReplay(1)
                   .refCount();
    }

    private filterOutcomes(level: VettingLevel): UnaryTransform<ReadonlyArray<VettingOutcome>> {
        return (outcomes) => outcomes.filter((o) => o.level === level);
    }

    private outcomeDescriptions(outcomes: ReadonlyArray<VettingOutcome>): ReadonlyArray<VettingMessage> {
        return outcomes.map((o) => o.message);
    }

    public $onInit(): void {
        this.watchResults();
    }

    private watchResults() {
        const hasErrors$ = this.outcomes$.map((o) => o.errors);
        const source$ = Observable.combineLatest(hasErrors$, this.form$);
        source$.takeUntil(this.destroyed$)
            .subscribe(([hasErrors, form]) => {
                // Now we have a result, we can say the form was initialized
                form.$setValidity(InitError, true, form);
                form.$setValidity(VettingError, !hasErrors, form);
            });
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
